// Notion mapping config — ported verbatim from the legacy v5.5 artifact.
// See REBUILD-SPEC.md §1.5 and §8/§9.
//
// NOTE: no Notion token is hardcoded here. The REST database_id and integration
// token are resolved server-side from Pages env (NOTION_TOKEN / NOTION_DB_ID).
// The collection/view ids below are stable identifiers safe to ship to the client.

/**
 * peekName -> Notion Guide page URL (the "Assigned Guide" relation target).
 * Verbatim from legacy GUIDE_PAGE_URL_BY_NAME (~line 4144). 19 entries — keeps
 * extras not in the active roster (Heather Conners, Hannah Johnson, Meg Kabotie,
 * Halen Roach, Justus Burkitt, Sully McGinnis) for relation resolution.
 * Keyed by Peek-side names, so "Bill Knots", "Devon Solovey", "Matthew Gore",
 * "James Hasapis", "Revel Lucien" are the lookup keys (NOT the availName variants).
 */
export const GUIDE_PAGE_URL_BY_NAME: Record<string, string> = {
  "Heather Conners": "https://app.notion.com/p/ac10830fb8ae45219f827012a32b351f",
  "Sean Golightly": "https://app.notion.com/p/36188c1cd62b80849467e96106dae96d",
  "Bill Knots": "https://app.notion.com/p/32f88c1cd62b80038ed5ffb1f6dc0925",
  "Meg Mezeske": "https://app.notion.com/p/32f88c1cd62b80d39fd1ffd679d85b6c",
  "Shane Harding": "https://app.notion.com/p/32f88c1cd62b80649b22f923cc211f83",
  "Hannah Johnson": "https://app.notion.com/p/32f88c1cd62b800a88dade8cf6f1b51b",
  "Devon Solovey": "https://app.notion.com/p/32f88c1cd62b80dc9d15e2151442aa5e",
  "Jade Marmorstein": "https://app.notion.com/p/21e88c1cd62b80f9b7a2d1bb89a9c1a3",
  "Meg Kabotie": "https://app.notion.com/p/21988c1cd62b80908b40c72a56db7e89",
  "Stephanie Tatum-Ford": "https://app.notion.com/p/1f588c1cd62b801a9375db4d723a9575",
  "Halen Roach": "https://app.notion.com/p/1ea88c1cd62b809b9bb8c4a91ab3a7f4",
  "Justus Burkitt": "https://app.notion.com/p/1e588c1cd62b8041a819eed3c79a6eb0",
  "Mike Miller": "https://app.notion.com/p/1e588c1cd62b8074a11ae3c6038abda3",
  "James Hasapis": "https://app.notion.com/p/1e588c1cd62b805caa1ee0ecb00ce07c",
  "Michelle Le Fevre": "https://app.notion.com/p/1e588c1cd62b80c1a641f15a0716ad50",
  "Revel Lucien": "https://app.notion.com/p/1e588c1cd62b80b9916bff50591c9273",
  "Steve Benning": "https://app.notion.com/p/1e588c1cd62b8082b4a4db169426f730",
  "Matthew Gore": "https://app.notion.com/p/1e588c1cd62b80218397c4268a9ffcae",
  "Sully McGinnis": "https://app.notion.com/p/1e588c1cd62b8053af51efe15b49b5db",
};

/**
 * Resolve a guide's Notion page id from peekName by stripping the URL down to the
 * trailing 32-hex page id. Ports the legacy `_pidFromUrl` (drop #/? fragments and
 * trailing slashes, take the last path segment). Returns undefined if unknown.
 */
export function guidePageIdByPeekName(peekName: string): string | undefined {
  const url = GUIDE_PAGE_URL_BY_NAME[peekName];
  if (!url) return undefined;
  return url
    .replace(/[#?].*$/, "")
    .replace(/\/+$/, "")
    .split("/")
    .pop();
}

/**
 * Notion availability-column first-name -> full availName bridge.
 *
 * KEYS MUST BE THE LIVE COLUMN NAMES. The Bookable Tours DB names its checkbox
 * columns by the guide's *availability-side* first name, which for two guides
 * differs from their Peek first name: the columns are "✨ Jamey" (James Hasapis)
 * and "✨ Monk" (Matthew Gore), and "✨ Dev" is a nickname for Devon Elia.
 * Verified against the live DB 2026-07-19 (26 columns). Keying these as "James"
 * / "Matthew" silently dropped both guides' availability from the board — the
 * bug Gracie reported.
 */
export const AVAIL_FIRST_TO_NAME: Record<string, string> = {
  Mike: "Mike Miller",
  Jamey: "Jamey Hasapis", // live column "✨ Jamey" (Peek name James Hasapis)
  Steve: "Steve Benning",
  Michelle: "Michelle Le Fevre",
  Sean: "Sean Golightly",
  Revel: "Revelucien",
  Shane: "Shane Harding",
  Stephanie: "Stephanie Tatum-Ford",
  Meg: "Meg Mezeske",
  Bill: "Bill Knotts",
  Dev: "Devon Elia", // nickname column "✨ Dev"
  Jade: "Jade Marmorstein",
  Monk: "Monk Gore", // live column "✨ Monk" (Peek name Matthew Gore)
};

// ---- Known Notion identifiers (stable; safe to ship client-side) ----

/**
 * The "Claude Sync — All markets Jul+" VIEW (database_id portion) for the
 * Bookable Tours 2026 DB. Legacy reached this via the view URL
 * https://app.notion.com/p/c95895d409c8459281375601ab07485a?v=...
 * The REST rebuild resolves the real database_id server-side from env
 * (NOTION_DB_ID); this constant documents the legacy view id origin.
 */
export const NOTION_BOOKABLE_VIEW_DB_ID = "c95895d409c8459281375601ab07485a";

/** Bookable Tours 2026 collection id (the DB the availability rows live in). */
export const NOTION_BOOKABLE_COLLECTION_ID =
  "869a0443-7c87-49fd-b062-93007eb8c700";

/** Guides collection id (the Assigned Guide relation target DB). */
export const NOTION_GUIDES_COLLECTION_ID =
  "1e588c1c-d62b-8055-afcd-000b5c28ffd2";
